import { useState } from "react";
import { Link } from "react-router-dom";
import { PRODUCTS } from "../../mocks/products";
import type { Product } from "../../mocks/products";
import ProductTable from "./components/ProductTable";
import ProductModal from "./components/ProductModal";
import DeleteModal from "./components/DeleteModal";

type Tab = "overview" | "export" | "import";
type ToastType = "success" | "error" | "info";
interface Toast { id: number; msg: string; type: ToastType }

export default function ManagePage() {
  const [products, setProducts] = useState<Product[]>([...PRODUCTS]);
  const [tab, setTab] = useState<Tab>("overview");
  const [editTarget, setEditTarget] = useState<Product | null | "new">(null);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [search, setSearch] = useState("");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [importText, setImportText] = useState("");
  const [importError, setImportError] = useState("");
  const [copied, setCopied] = useState(false);

  const showToast = (msg: string, type: ToastType = "success") => {
    const id = Date.now();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  };

  const filtered = products.filter(
    (p) =>
      search.trim() === "" ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.brand.toLowerCase().includes(search.toLowerCase())
  );

  // CRUD
  const handleSave = (product: Product) => {
    setProducts((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) return prev.map((p) => (p.id === product.id ? product : p));
      return [...prev, product];
    });
    showToast(editTarget === "new" ? "商品已新增！" : "商品已更新！");
    setEditTarget(null);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setProducts((prev) => prev.filter((p) => p.id !== deleteTarget.id));
    showToast(`「${deleteTarget.name}」已刪除`, "info");
    setDeleteTarget(null);
  };

  // Export
  const exportJson = JSON.stringify(products, null, 2);
  const handleCopy = () => {
    navigator.clipboard.writeText(exportJson).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Import via paste
  const handleApplyImport = () => {
    setImportError("");
    if (!importText.trim()) {
      setImportError("請先貼上 JSON 資料");
      return;
    }
    try {
      const data = JSON.parse(importText) as Product[];
      if (!Array.isArray(data)) throw new Error();
      setProducts(data);
      setImportText("");
      showToast(`已套用 ${data.length} 件商品資料！`);
      setTab("overview");
    } catch {
      setImportError("JSON 格式有誤，請確認後重試");
    }
  };

  const avgDiscount = Math.round(products.reduce((s, p) => s + p.discount, 0) / (products.length || 1));
  const avgRating = (products.reduce((s, p) => s + p.rating, 0) / (products.length || 1)).toFixed(1);

  const TABS: { id: Tab; label: string; icon: string }[] = [
    { id: "overview", label: "商品總覽", icon: "ri-grid-line" },
    { id: "export", label: "匯出商品資料", icon: "ri-download-2-line" },
    { id: "import", label: "匯入商品資料", icon: "ri-upload-2-line" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1B4332] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-white/15 rounded-lg">
              <i className="ri-settings-3-line text-white text-base"></i>
            </div>
            <div>
              <p className="text-white text-sm font-semibold leading-tight">商品批次管理</p>
              <p className="text-white/50 text-xs">Demo Manage Panel · 僅限內部使用</p>
            </div>
          </div>
          <Link
            to="/shop"
            className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-sm"></i>
            回到商品頁
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Workflow hint */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center text-amber-500 flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-base"></i>
            </div>
            <div>
              <p className="text-amber-700 font-semibold text-sm mb-2">發佈流程說明</p>
              <ol className="space-y-1">
                {[
                  "前往 /shop 頁面，點右下角「進入編輯模式」完成商品編排",
                  "回到此頁，切到「匯出商品資料」分頁，複製 JSON",
                  "將 JSON 資料貼給 AI，請它「幫我把這份商品資料存入程式碼」",
                  "AI 更新後，商品資料就會永久固定，所有人打開都看到一樣的內容",
                ].map((step, i) => (
                  <li key={i} className="text-amber-600 text-sm flex items-start gap-2">
                    <span className="font-semibold flex-shrink-0">{i + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1.5 mb-6 w-fit">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                tab === t.id
                  ? "bg-[#1B4332] text-white shadow-sm"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              <i className={`${t.icon} text-sm`}></i>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab: 商品總覽 ── */}
        {tab === "overview" && (
          <div className="space-y-5">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { icon: "ri-box-3-line", label: "商品總數", value: String(products.length), unit: "件", iconBg: "bg-emerald-50", iconColor: "text-emerald-600" },
                { icon: "ri-price-tag-3-line", label: "平均折扣", value: String(avgDiscount), unit: "%", iconBg: "bg-red-50", iconColor: "text-red-500" },
                { icon: "ri-star-line", label: "平均評分", value: avgRating, unit: "分", iconBg: "bg-amber-50", iconColor: "text-amber-500" },
                { icon: "ri-add-circle-line", label: "新增商品", value: "+", unit: "", iconBg: "bg-[#1B4332]/8", iconColor: "text-[#1B4332]" },
              ].map((stat, i) => (
                <div
                  key={stat.label}
                  onClick={i === 3 ? () => setEditTarget("new") : undefined}
                  className={`bg-white border border-gray-100 rounded-xl p-5 flex items-center gap-4 ${i === 3 ? "cursor-pointer hover:border-[#1B4332]/30 hover:bg-[#1B4332]/4 transition-colors" : ""}`}
                >
                  <div className={`w-11 h-11 flex items-center justify-center ${stat.iconBg} rounded-xl flex-shrink-0`}>
                    <i className={`${stat.icon} text-xl ${stat.iconColor}`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">{stat.label}</p>
                    <p className="text-xl font-bold text-gray-800">
                      {stat.value}<span className="text-sm font-normal text-gray-400 ml-0.5">{stat.unit}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Search + Add */}
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-white border border-gray-100 rounded-xl px-4 py-2.5 flex items-center gap-2">
                <i className="ri-search-line text-gray-400 text-sm flex-shrink-0"></i>
                <input
                  type="text" value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="搜尋商品名稱或品牌..."
                  className="flex-1 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none bg-transparent"
                />
                {search && (
                  <button onClick={() => setSearch("")} className="text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0">
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>
              <button
                onClick={() => setEditTarget("new")}
                className="flex items-center gap-1.5 px-4 h-10 bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#2D6A4F] transition-colors rounded-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                新增商品
              </button>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
              <ProductTable
                products={filtered}
                onEdit={(p) => setEditTarget(p)}
                onDelete={(p) => setDeleteTarget(p)}
              />
            </div>
          </div>
        )}

        {/* ── Tab: 匯出商品資料 ── */}
        {tab === "export" && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-1">匯出商品資料</h2>
            <p className="text-sm text-gray-400 mb-5">複製下方 JSON 資料，交給 AI 寫入程式碼，讓商品永久固定</p>

            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-5 flex items-start gap-3">
              <i className="ri-information-line text-sky-500 text-base flex-shrink-0 mt-0.5"></i>
              <p className="text-sky-700 text-sm leading-relaxed">
                複製後，貼給 AI 並說：<strong>「幫我把這份商品資料存入程式碼」</strong>，AI 即可更新 src/mocks/products.ts，讓所有人看到相同內容。
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-2">
                  <i className="ri-code-s-slash-line text-gray-400 text-sm"></i>
                  <span className="text-xs font-semibold text-gray-500 tracking-wider uppercase">products.json</span>
                  <span className="text-xs text-gray-400">— {products.length} 件商品</span>
                </div>
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1.5 px-4 h-8 text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    copied ? "bg-emerald-500 text-white" : "bg-[#1B4332] text-white hover:bg-[#2D6A4F]"
                  }`}
                >
                  <i className={`text-sm ${copied ? "ri-check-line" : "ri-clipboard-line"}`}></i>
                  {copied ? "已複製！" : "複製 JSON"}
                </button>
              </div>
              <textarea
                readOnly
                value={exportJson}
                className="w-full h-80 px-4 py-4 text-xs font-mono text-gray-600 bg-white resize-none focus:outline-none leading-relaxed"
                style={{ scrollbarWidth: "thin" }}
              />
            </div>
          </div>
        )}

        {/* ── Tab: 匯入商品資料 ── */}
        {tab === "import" && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-1">匯入商品資料</h2>
            <p className="text-sm text-gray-400 mb-5">貼上 JSON 格式的商品資料，套用到目前的瀏覽器預覽</p>

            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-5 flex items-start gap-3">
              <i className="ri-information-line text-sky-500 text-base flex-shrink-0 mt-0.5"></i>
              <p className="text-sky-700 text-sm leading-relaxed">
                <strong>適合場景：</strong>AI 幫你調整後給你的新商品資料，可以貼在這裡先預覽效果，確認後再請 AI 寫入程式碼。
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                <i className="ri-code-s-slash-line text-gray-400 text-sm"></i>
                <span className="text-xs font-semibold text-gray-500 tracking-wider uppercase">貼入 JSON</span>
              </div>
              <textarea
                value={importText}
                onChange={(e) => { setImportText(e.target.value); setImportError(""); }}
                placeholder={`貼上 JSON 資料，例如：[{"id":"p1","name":"...","price":10000,...}]`}
                className="w-full h-72 px-4 py-4 text-sm font-mono text-gray-700 placeholder:text-gray-300 bg-white resize-none focus:outline-none leading-relaxed"
                style={{ scrollbarWidth: "thin" }}
              />
            </div>

            {importError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
                <i className="ri-error-warning-line text-red-400 text-sm"></i>
                <p className="text-red-500 text-sm">{importError}</p>
              </div>
            )}

            <button
              onClick={handleApplyImport}
              disabled={!importText.trim()}
              className="flex items-center gap-2 px-6 h-11 bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#2D6A4F] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 rounded-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-sm"></i>
              套用商品資料
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {editTarget !== null && (
        <ProductModal
          product={editTarget === "new" ? null : editTarget}
          onSave={handleSave}
          onClose={() => setEditTarget(null)}
        />
      )}
      {deleteTarget !== null && (
        <DeleteModal
          product={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {/* Toasts */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-[100]">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-medium shadow-lg ${
              t.type === "success" ? "bg-emerald-500 text-white"
              : t.type === "error" ? "bg-red-500 text-white"
              : "bg-gray-800 text-white"
            }`}
          >
            <i className={`text-base ${t.type === "success" ? "ri-check-line" : t.type === "error" ? "ri-error-warning-line" : "ri-information-line"}`}></i>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}
